import Module from '@/store/resource/common/module';
import Org from '@/store/resource/org';
import api from '@/factories/api';

let module = new Module(new Org());

import qs from 'qs';

module.add('state', {
  featureUsageLoaded: false,
  featureUsage: {
    campaigns: null,
    workflows: null,
    workflow_size: null,
    sms: null,
    users: null,
  },
  orgBilling: null,
  orgSettings: null,
});

module.add('actions', {
  async loadOrgSettings({ commit, state }, force = false) {
    if (state.orgSettings && !force) {
      return state.orgSettings;
    }

    try {
      const res = await api().get('org');
      commit('setOrgSettings', res);
      return res;
    } catch (err) {
      throw err;
    }
  },

  async updateOrgSettings({ dispatch }, data) {
    try {
      const res = await api().put('org', data);
      const { public_key } = res || {};
      if (public_key) {
        this.commit('org/updateOrgSettings', {
          public_encryption_key: public_key,
        });
      }

      dispatch('loadOrgSettings', true);
      return res;
    } catch (err) {
      throw err;
    }
  },

  // Sudo-specific actions for backend settings (support users only)
  // These do NOT update shared orgSettings state to avoid conflicts
  async loadOrgSettingsSudo() {
    try {
      const res = await api().get('org?sudo=1');
      return res;
    } catch (err) {
      throw err;
    }
  },

  async updateOrgSettingsSudo(_, data) {
    try {
      const res = await api().put('org?sudo=1', data);
      return res;
    } catch (err) {
      throw err;
    }
  },

  async getAllFeaturesUsage({ commit }) {
    try {
      const res = await api().get(`org/usage`);
      commit('setAllFeaturesUsage', res);
      return res;
    } catch (err) {
      throw err;
    }
  },

  async getFeatureUsage({ commit }, params) {
    try {
      const strParams = qs.stringify(params);
      const res = await api().get(`org/usage?${strParams}`);
      commit('setFeatureUsage', {
        type: params.type,
        usage: res[params.type],
      });
      return res[params.type];
    } catch (err) {
      throw err;
    }
  },

  async getOrgBilling({ commit }, shareToken) {
    commit('loading');
    try {
      const billing = await api({ shareToken }).get('org/billing');
      commit('setOrgBilling', billing);
    } catch (err) {
      throw err;
    } finally {
      commit('loading', false);
    }
  },

  async updateOrgBilling({ dispatch }, { stripeToken, sharedToken }) {
    try {
      await api({
        params: { token: sharedToken },
      }).put('org/billing', { token: stripeToken });
      dispatch('getOrgBilling');
    } catch (err) {
      throw err;
    }
  },

  async notifyAboutBilling(_, user_ids) {
    if (!user_ids.length) return;

    await api().post('org/billing_notify', { user_ids });
  },

  async updateOrgPlan({ commit }, data) {
    return await api().put('org/plan', data);
  },

  async getOrgInvoices({}) {
    try {
      const data = await api().get('org/invoices');
      return data?.invoices || [];
    } catch (err) {
      throw err;
    }
  },
});

module.add('getters', {
  orgPublicKey(state) {
    return state.orgSettings?.public_encryption_key;
  },
  fieldSections(state) {
    const sections = state.orgSettings?.meta?.field_sections || [];
    return [
      {
        title: 'Default',
        slug: '',
        locked: true,
      },
      ...sections,
    ];
  },
  campaignSections(state) {
    const sections = state.orgSettings?.meta?.portal_sections || [];
    return [
      {
        title: 'No Portal Section',
        slug: '',
        id: null,
        locked: true,
      },
      ...sections.map((section) => ({
        ...section,
        // Use id if available (new backend), otherwise use slug (old backend)
        id: section.id || section.slug,
      })),
    ];
  },
});

module.add('mutations', {
  setFeatureUsage(state, data) {
    const { type, usage } = data;

    //setting featureSUsage[type] was not forcing reactivity
    state.featureUsage = {
      ...state.featureUsage,
      [type]: usage,
    };
  },
  setAllFeaturesUsage(state, data) {
    state.featureUsage = data;
    state.featureUsageLoaded = true;
  },
  setOrgBilling(state, data) {
    state.orgBilling = data;
  },
  setOrgSettings(state, data) {
    state.orgSettings = { ...state.orgSettings, ...data };
  },
});

export default module.create();
